<template>
  <div class="configuration-wrapper">
    <Panel>
      <template #header>
        <div class="panel-header-content">
          <h4>{{ panelTitle }}</h4>
        </div>
      </template>

      <template #icons>
        <i
          v-if="!isTeaserTemplate"
          class="far fa-times icons-header-panel"
          @click="hideLeftToolbar"
        />
      </template>

      <!-- Teaser display settings -->
      <template v-if="isTeaserTemplate">
        <div class="field-group pt-3">
          <div class="teaser-display-options">
            <div
              v-for="option in displayModeOptions"
              :key="option.value"
              class="display-option"
              :class="{ 'display-option--selected': displayMode === option.value }"
              @click="setDisplayMode(option.value)"
            >
              <div class="display-option__radio">
                <div
                  class="display-option__radio-inner"
                  :class="{ 'display-option__radio-inner--selected': displayMode === option.value }"
                />
              </div>
              <div class="display-option__content">
                <div class="display-option__title">{{ option.label }}</div>
                <div
                  v-if="option.description"
                  class="display-option__description"
                >
                  {{ option.description }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Regular template name input -->
      <div
        v-if="!isTeaserTemplate"
        class="field-group pt-3"
      >
        <div>
          <div class="field-group-content">
            <div class="field-wrapper">
              <label class="block mb-3">
                {{ informationNameInputLabel }}
              </label>
              <div class="p-fluid flex">
                <InputText
                  v-model="name"
                  :disabled="isGamePrize"
                />
              </div>
              <FieldErrors
                :key="componentFieldErrorsKey"
                :errors="formValidationErrors"
                field="name"
              />
            </div>
          </div>
        </div>
      </div>
      <template v-if="isEmailTemplate && !selectedSyncElement">
        <div class="field-group pt-3">
          <div>
            <div class="field-group-content">
              <div class="field-wrapper">
                <label class="block mb-3">
                  {{ t('templateBuilder.fields.emailSubject', null) }}
                </label>
                <div class="p-fluid flex">
                  <LocalizedTextInput
                    id="subject"
                    v-model="subject"
                    :display-emojis="true"
                    :display-variables="true"
                    :display-translate="true"
                    :display-ai-button="true"
                    :ai-params="{ aiContentType: 2 }"
                    :variables-list="variables.allVariables"
                    class="w-full"
                  />
                </div>
                <FieldErrors
                  :key="componentFieldErrorsKey"
                  :errors="formValidationErrors"
                  field="subject"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="field-group pt-3">
          <div>
            <div class="field-group-content">
              <div class="field-wrapper">
                <label class="block mb-3">
                  {{ t('templateBuilder.fields.previewText', null) }}
                  <i
                  v-tooltip="{ value: t('templateBuilder.fields.previewTextHelp', null), escape: true, class: 'tooltip-icons-outside' }"
                  class="fas fa-question-circle"
                  data-html="true"
                  data-toggle="tooltip"
                  aria-hidden="true"
                />
                </label>
                <div class="p-fluid flex">
                  <LocalizedTextInput
                    id="previewText"
                    v-model="previewText"
                    :display-emojis="true"
                    :display-variables="true"
                    :display-translate="true"
                    :display-ai-button="true"
                    :ai-params="{ aiContentType: 6 }"
                    :variables-list="variables.allVariables"
                    class="w-full"
                  />
                </div>
                <FieldErrors
                  :key="componentFieldErrorsKey"
                  :errors="formValidationErrors"
                  field="previewText"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="field-group pt-3">
          <div>
            <div class="field-group-content">
              <div class="field-wrapper">
                <label class="block mb-3">
                  {{ t('templateBuilder.fields.senderEmail', null) }}
                </label>
                <div class="p-fluid flex">
                  <LocalizedTextInput
                    id="fromEmail"
                    v-model="fromEmail"
                    type="auto_complete"
                    :complete="(query) => searchValuesForAutocompletion('senderAddress', query, myDataAutoCompletion)"
                    class="w-full"
                    :continue-reloading="true"
                  />
                </div>
                <span style="font-size: 12px; color: #495057;">
                  {{ t('emailDomainValid.message', null) }}
                  <span
                    style="cursor: pointer; text-decoration: underline; color: blue;"
                    @click="goToDomainPage"
                  >
                    {{ t('emailDomainValid.click', null) }}
                  </span></span>
                <FieldErrors
                  :key="componentFieldErrorsKey"
                  :errors="formValidationErrors"
                  field="fromEmail"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="field-group pt-3">
          <div>
            <div class="field-group-content">
              <div class="field-wrapper">
                <label class="block mb-3">
                  {{ t('templateBuilder.fields.senderName', null) }}
                </label>
                <div class="p-fluid flex">
                  <LocalizedTextInput
                    id="fromName"
                    v-model="fromName"
                    type="auto_complete"
                    :complete="(query) => searchValuesForAutocompletion('senderName', query, myDataAutoCompletion)"
                    class="w-full"
                    :display-emojis="true"
                  />
                </div>
                <FieldErrors
                  :key="componentFieldErrorsKey"
                  :errors="formValidationErrors"
                  field="fromName"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-if="isSmsTemplate && !selectedSyncElement">
        <div class="field-group pt-3">
          <div>
            <div class="field-group-content">
              <div class="field-wrapper">
                <label class="block mb-3">
                  {{ t('templateBuilder.fields.senderNameSms', null) }}
                </label>
                <div class="p-fluid flex">
                  <LocalizedTextInput
                    id="fromName"
                    v-model="fromName"
                    :display-emojis="true"
                    type="auto_complete"
                    :complete="(query) => searchValuesForAutocompletion('senderName', query, myDataAutoCompletion)"
                    class="w-full"
                  />
                </div>
                <FieldErrors
                  :key="componentFieldErrorsKey"
                  :errors="formValidationErrors"
                  field="fromName"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </Panel>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent, onMounted,
  Ref,
  ref,
  watch,
  onBeforeMount,
} from 'vue';

import { useStore } from '@/store';
import Tooltip from 'primevue/tooltip';
import InputText from 'primevue/inputtext';
import Panel from 'primevue/panel';
import { useI18n } from 'vue-i18n';
import variables from '@/components/template-builder/utils/variables-list';
import {
  hideLeftToolbar,
  markTemplateAsEdited,
  TemplateEditorState as state,
  togglePanel,
  updateTemplateInformations,
  setTemplateConfigurationKey,
  isGamePrizeTemplate,
} from '@/composables/template-editor/TemplateEditor';
import { TemplateTypeEnum } from '@/composables/shop/Templates';
import {
  StringMap,
  TemplateInformations,
  TemplateParentTypeEnum,
  SelectedSyncElement,
  ElementPartTypeEnum,
} from '@/types';
import { getShopDefaultLang, Lang, UserState } from '@/composables/User';
import { getTemplateParentTypeByType } from '@/components/template-builder/utils/helpers';
import LocalizedTextInput from '@/components/fields/LocalizedTextInput.vue';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import {
  loadSenderAddresses,
  loadSenderNames,
  searchValuesForAutocompletion,
} from '@/composables/autoCompletionValuesMedia';
import getShopsDomains from '@/composables/shop/ShopsSendDomains';
import { OperatorType } from '@/types/generated-types/graphql';
import { useRouter } from 'vue-router';

export default defineComponent({
  name: 'InformationPanel',

  components: {
    FieldErrors,
    LocalizedTextInput,
    InputText,
    Panel,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    formValidation: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },

  setup(props: { formValidation: any }) {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();

    const selectedSyncElement = computed<SelectedSyncElement>(() => store.getters['liveEditor/getSelectedSyncElement']);

    const name: Ref<string> = ref(!selectedSyncElement.value ? state.template?.informations.name ?? '' : selectedSyncElement.value.elementName ?? '');
    const languages: string[] = UserState.activeShop?.langs.map((language: Lang) => language.id) ?? [];
    const defaultLang: string = state.template?.informations.lang ?? '';
    const defaultLanguage: string = getShopDefaultLang();
    const subject: Ref<StringMap> = ref({});
    const previewText: Ref<StringMap> = ref({});
    const fromEmail: Ref<StringMap> = ref({});
    const fromName: Ref<StringMap> = ref({});

    const formValidationErrors = ref({});
    const componentFieldErrorsKey = ref(0);

    languages.forEach((language: string) => {
      if (getTemplateParentTypeByType(state.template?.type ?? '') === TemplateParentTypeEnum.EMAIL) {
        if (state.template?.informations.subject) {
          subject.value[language] = state.template?.informations.subject[language] ?? state.template?.informations.subject[defaultLanguage] ?? '';
        }

        if (state.template?.informations.previewText) {
          previewText.value[language] = state.template?.informations.previewText[language] ?? state.template?.informations.previewText[defaultLanguage] ?? '';
        }

        if (state.template?.informations.fromEmail) {
          fromEmail.value[language] = state.template?.informations.fromEmail[language] ?? state.template?.informations.fromEmail[defaultLanguage] ?? '';
        }

        if (state.template?.informations.fromName) {
          fromName.value[language] = state.template?.informations.fromName[language] ?? state.template?.informations.fromName[defaultLanguage] ?? '';
        }
      } else if (getTemplateParentTypeByType(state.template?.type ?? '') === TemplateParentTypeEnum.SMS && state.template?.informations.fromName) {
        fromName.value[language] = state.template?.informations.fromName[language] ?? state.template?.informations.fromName[defaultLanguage] ?? '';
      }
    });

    const isEmailTemplate = computed(() => getTemplateParentTypeByType(state.template?.type ?? '') === TemplateParentTypeEnum.EMAIL);
    const isSmsTemplate = computed(() => getTemplateParentTypeByType(state.template?.type ?? '') === TemplateParentTypeEnum.SMS);
    const isTeaserTemplate = computed(() => state.template?.type === TemplateTypeEnum.TEASER);
    const isGamePrize = computed(() => isGamePrizeTemplate());

    const panelTitle = computed(() => {
      if (isTeaserTemplate.value) {
        return t('templateBuilder.teaser.displaySettings');
      }
      return t('templateBuilder.toolbar.tooltip.information');
    });

    // Teaser display mode options
    const displayModeOptions = computed(() => [
      {
        value: 'after_close',
        label: t('templateBuilder.teaser.displayMode.afterClose'),
        description: null,
      },
      {
        value: 'before_display',
        label: t('templateBuilder.teaser.displayMode.beforeDisplay'),
        description: t('templateBuilder.teaser.displayMode.beforeDisplayDesc'),
      },
      {
        value: 'both',
        label: t('templateBuilder.teaser.displayMode.both'),
        description: t('templateBuilder.teaser.displayMode.bothDesc'),
      },
    ]);

    const displayMode = ref<string>(state.template?.configuration?.displayMode || 'after_close');

    const setDisplayMode = (mode: string) => {
      displayMode.value = mode;
      setTemplateConfigurationKey('displayMode', mode);
      markTemplateAsEdited();
    };

    onBeforeMount(() => {
      if (isTeaserTemplate.value && !state.template?.configuration?.displayMode) {
        setTemplateConfigurationKey('displayMode', 'after_close');
      }
    });

    watch([name, subject.value, previewText.value, fromEmail.value, fromName.value], () => {
      if (!selectedSyncElement.value) {
        const informations: TemplateInformations = {
          name: name.value,
          lang: defaultLang,
        };

        if (getTemplateParentTypeByType(state.template?.type ?? '') === TemplateParentTypeEnum.EMAIL) {
          informations.subject = subject.value;
          informations.previewText = previewText.value;
          informations.fromEmail = fromEmail.value;
          informations.fromName = fromName.value;
        } else if (getTemplateParentTypeByType(state.template?.type ?? '') === TemplateParentTypeEnum.SMS) {
          informations.fromName = fromName.value;
        }

        updateTemplateInformations(informations);
        markTemplateAsEdited();
      } else {
        store.commit('liveEditor/setSelectedSyncElementName', name.value);
      }
    });

    const goToDomainPage = () => {
      const routePath = router.resolve({ path: '/shop/send-domains' }).href;
      window.open(routePath, '_blank');
      togglePanel('informationPanel');
    };

    const myDataAutoCompletion: Ref<any> = ref({
      senderNames: [],
      senderAddresses: [],
      domainsOfShop: [],
    });

    onMounted(async () => {
      myDataAutoCompletion.value.senderNames = await loadSenderNames(state.template?.type ?? 'email');

      if (state.template?.type === 'email') {
        myDataAutoCompletion.value.senderAddresses = await loadSenderAddresses();
        myDataAutoCompletion.value.domainsOfShop = await getShopsDomains(0, [{
          field: 'domain_verified',
          operator: OperatorType.Equals,
          value: true,
        }]);
      }

      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;
    });

    watch(() => props.formValidation, () => {
      formValidationErrors.value = props.formValidation;
      componentFieldErrorsKey.value += 1;
    });

    const informationNameInputLabel = computed(() => {

      if (selectedSyncElement.value) {
        if (selectedSyncElement.value.elementPartType === ElementPartTypeEnum.STYLES) {
          return t('templateBuilder.fields.syncElementNameStyles')
        }
        return t('templateBuilder.fields.syncElementName');
      }
      return t('templateBuilder.fields.templateName', null);
    });

    return {
      t,
      name,
      subject,
      previewText,
      fromEmail,
      fromName,
      variables,
      isEmailTemplate,
      isSmsTemplate,
      isTeaserTemplate,
      panelTitle,
      displayModeOptions,
      displayMode,
      setDisplayMode,
      hideLeftToolbar,
      isGamePrize,
      formValidationErrors,
      componentFieldErrorsKey,
      myDataAutoCompletion,
      searchValuesForAutocompletion,
      goToDomainPage,
      selectedSyncElement,
      informationNameInputLabel,
    };
  },
});
</script>

<style lang="scss" scoped>
.icons-header-panel {
  cursor: pointer;
  color: #607D8B;
}

.field-group-content {
  .field-wrapper:not(:last-child) {
    margin-bottom: 1.5rem;
  }
}

.teaser-display-options {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0 1rem;
}

.display-option {
  display: flex;
  align-items: flex-start;
  padding: 12px 16px;
  cursor: pointer;
  border: 1px solid #dee2e6;
  margin-top: -1px;
  background: #fff;
  transition: all 0.2s ease;

  &:first-child {
    border-radius: 6px 6px 0 0;
    margin-top: 0;
  }

  &:last-child {
    border-radius: 0 0 6px 6px;
  }

  &:hover {
    background: #f8f9fa;
  }

  &--selected {
    border: 1px solid #8bc34a;
    background: #f9fdf5;
    position: relative;
    z-index: 1;
  }

  &__radio {
    width: 18px;
    height: 18px;
    border: 2px solid #adb5bd;
    border-radius: 50%;
    margin-right: 12px;
    margin-top: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .display-option--selected & {
      border-color: #8bc34a;
    }
  }

  &__radio-inner {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: transparent;
    transition: background 0.2s ease;

    &--selected {
      background: #8bc34a;
    }
  }

  &__content {
    flex: 1;
  }

  &__title {
    font-weight: 500;
    color: #495057;
    font-size: 13px;
    line-height: 1.4;
  }

  &__description {
    font-size: 12px;
    color: #6c757d;
    margin-top: 4px;
    line-height: 1.4;
  }
}
</style>
